import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import type { PieLabelRenderProps } from "recharts";
import { Monitor, Smartphone, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { DeviceItem } from "../types/PerformanceTypes";
import { DEVICE_COLORS } from "../../../constants/constants";
import { fmtValue } from "../utils/PerformanceUtils";
import Card from "../ui/Card";
import SectionTitle from "../ui/SectionTitle";
import ChartTooltip from "../ui/ChartTooltip";

 
const DeltaBadge = ({ current, previous }: { current: number; previous: number }) => {
  if (!previous) return null;
  const pct = Math.round(((current - previous) / previous) * 100);
  const up  = pct > 0;
  const eq  = pct === 0;
  if (eq) return (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-400">
      <Minus size={9} /> 0%
    </span>
  );
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold
      ${up ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
      {up ? <TrendingUp size={9} strokeWidth={2.5} /> : <TrendingDown size={9} strokeWidth={2.5} />}
      {up ? "+" : ""}{pct}%
    </span>
  );
};

 
const renderDeviceLabel = (props: PieLabelRenderProps): string => {
  const name       = String(props.name ?? "");
  const percentage = (props as any).payload?.percentage ?? 0;
  return `${name} ${percentage}%`;
};

 
interface DevicesTabProps {
  data:                DeviceItem[];
  compareEnabled?:     boolean;
  compareData?:        DeviceItem[];
  primaryPeriodLabel?:  string;
  comparePeriodLabel?:  string;
}



const DevicesTab = ({
  data,
  compareEnabled,
  compareData,
  primaryPeriodLabel  = "Période A",
  comparePeriodLabel  = "Période B",
}: DevicesTabProps) => (
  <div className="flex flex-wrap gap-4 animate-[gbpFadeIn_0.2s_ease]">

     <Card className="flex-[1_1_240px]">
      <SectionTitle>Répartition Desktop / Mobile</SectionTitle>
      <ResponsiveContainer width="100%" height={230}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%"
               innerRadius={65} outerRadius={98}
               paddingAngle={5} dataKey="value"
               label={renderDeviceLabel} labelLine>
            {data.map((_, i) => <Cell key={i} fill={DEVICE_COLORS[i % 2]} />)}
          </Pie>
          <ChartTooltip />
        </PieChart>
      </ResponsiveContainer>

       {compareEnabled && (
        <p className="text-center text-[11px] text-blue-600 font-semibold mt-1 mb-0">
          {primaryPeriodLabel}
        </p>
      )}
    </Card>

     {compareEnabled && compareData && (
      <Card className="flex-[1_1_240px]">
        <SectionTitle>Répartition (comparaison)</SectionTitle>
        <ResponsiveContainer width="100%" height={230}>
          <PieChart>
            <Pie data={compareData} cx="50%" cy="50%"
                 innerRadius={65} outerRadius={98}
                 paddingAngle={5} dataKey="value"
                 label={renderDeviceLabel} labelLine>
              {compareData.map((_, i) => (
                <Cell key={i} fill={DEVICE_COLORS[i % 2]} fillOpacity={0.45} />
              ))}
            </Pie>
            <ChartTooltip />
          </PieChart>
        </ResponsiveContainer>
        <p className="text-center text-[11px] text-violet-500 font-semibold mt-1 mb-0">
          {comparePeriodLabel}
        </p>
      </Card>
    )}

     <Card className="flex-[2_1_320px]">
      <SectionTitle>Détail</SectionTitle>
      <div className="flex flex-col gap-5 mt-2">
        {data.map((item, i) => {
          const prevItem = compareData?.[i];
          return (
            <div key={item.name}>
               <div className="flex items-center justify-between mb-2">
                <span className="flex items-center gap-2 text-[13px] font-medium text-gray-700">
                  {i === 0
                    ? <Monitor    size={14} color={DEVICE_COLORS[i]} />
                    : <Smartphone size={14} color={DEVICE_COLORS[i]} />
                  }
                  {item.name}
                </span>
                <div className="flex items-center gap-2">
                  {compareEnabled && prevItem && (
                    <DeltaBadge current={item.value} previous={prevItem.value} />
                  )}
                  <span className="text-[13px] font-semibold text-slate-900">
                    {fmtValue(item.value)}{" "}
                    <span className="text-[11px] font-normal text-slate-400">
                      ({item.percentage}%)
                    </span>
                  </span>
                </div>
              </div>

               <div className="relative h-2.5 bg-slate-100 rounded-full overflow-hidden">
                 {compareEnabled && prevItem && (
                  <div
                    className="absolute inset-y-0 left-0 rounded-full opacity-30 transition-[width] duration-500 ease-out"
                    style={{ width: `${prevItem.percentage}%`, background: DEVICE_COLORS[i] }}
                  />
                )}
                {/* Primary bar */}
                <div
                  className="absolute inset-y-0 left-0 h-full rounded-full transition-[width] duration-[600ms] ease-out"
                  style={{ width: `${item.percentage}%`, background: DEVICE_COLORS[i] }}
                />
              </div>

              {/* Compare value */}
              {compareEnabled && prevItem && (
                <div className="flex items-center gap-2 mt-1.5">
                  <div className="h-0.5 w-4 rounded-full opacity-40" style={{ background: DEVICE_COLORS[i] }} />
                  <span className="text-[11px] text-slate-400">
                    {comparePeriodLabel}: <span className="font-semibold text-slate-500">{fmtValue(prevItem.value)}</span>
                    <span className="ml-1">({prevItem.percentage}%)</span>
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>

  </div>
);

export default DevicesTab;